import type { HTMLAttributes, ReactNode } from 'react';
import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './ChatMessageBubble.css';

export type ChatMessageRole = 'user' | 'assistant' | 'tool';

const DEFAULT_ICON_BY_ROLE: Record<ChatMessageRole, string> = {
  user: 'user-circle',
  assistant: 'robot',
  tool: 'tool',
};

/** Tooltip shown on the role icon (and the default accessible label). */
export const CHAT_MESSAGE_ROLE_LABELS: Record<ChatMessageRole, string> = {
  user: 'User',
  assistant: 'Assistant',
  tool: 'Tool',
};

/** One turn of a conversation trajectory (Figma "Data Editor — agentic example" bubbles). */
export type ChatMessageBubbleProps = {
  /** Who produced the message; drives color, icon and alignment. */
  role: ChatMessageRole;
  /** Message body — plain text or rich content (e.g. `ExpandableText`). */
  children?: ReactNode;
  /** Overrides the role's default foundation icon. */
  iconSlug?: string;
  /** Hides the 24px role icon. */
  showIcon?: boolean;
  /** Accessible label and icon tooltip; defaults to `User` / `Assistant` / `Tool`. */
  roleLabel?: string;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'role'>;

/**
 * ChatMessageBubble — a rounded 12px message with a 24px role icon.
 *
 * - `user`: blue-200 tint, left-aligned, `user-circle` icon (Figma 3966:24164).
 * - `assistant`: green tint `#EAFAF2`, right-aligned, max 480px, `robot` icon (Figma 3966:24168).
 * - `tool`: neutral subtle tint, left-aligned, orange `tool` icon — fallback for a tool
 *   result that is not paired with a `ToolCallCard`.
 *
 * Hovering the role icon shows a tooltip naming the role (`roleLabel`).
 */
export function ChatMessageBubble({
  role,
  children,
  iconSlug,
  showIcon = true,
  roleLabel,
  className = '',
  ...rest
}: ChatMessageBubbleProps) {
  const classes = ['spark-chat-bubble-row', `spark-chat-bubble-row-${role}`, className].filter(Boolean).join(' ');
  const label = roleLabel ?? CHAT_MESSAGE_ROLE_LABELS[role];

  return (
    <div className={classes} {...rest}>
      <div className={`spark-chat-bubble spark-chat-bubble-${role}`} data-role={role} aria-label={label}>
        {showIcon ? (
          <TooltipTrigger text={label} placement="top" className="spark-chat-bubble-icon-trigger">
            <SparkIcon slug={iconSlug ?? DEFAULT_ICON_BY_ROLE[role]} size="medium" className="spark-chat-bubble-icon" />
          </TooltipTrigger>
        ) : null}
        <div className="spark-chat-bubble-body">{children}</div>
      </div>
    </div>
  );
}
